<template>
  <div class="seal-touch-detect-container">
    <van-nav-bar title="印章触点检测" left-arrow @click-left="$router.back()" />

    <!-- 上方实时显示：当前触点个数 + 每个触点位置 -->
    <div class="realtime-info">
      <p class="count-line">
        <strong>当前触点：</strong>
        <span class="count-num">{{ touchPoints.length }}</span> 个
      </p>
      <div v-if="touchPoints.length > 0" class="points-list">
        <p v-for="(point, idx) in touchPoints" :key="idx" class="point-line">
          点{{ idx + 1 }}： [{{ point[0] }}, {{ point[1] }}] <span class="coord-hint">（标准坐标 800×600）</span>
        </p>
      </div>
      <p v-else class="no-touch">将印章与屏幕接触，此处实时显示触点数量与位置</p>
    </div>

    <!-- 触摸区域：整块容器监听，减少边缘漏点；touch-action:none 降低被合并为 1 个点的概率 -->
    <div
      ref="canvasContainer"
      class="canvas-container touch-capture"
      @touchstart.prevent="syncTouches"
      @touchmove.prevent="syncTouches"
      @touchend.prevent="syncTouches"
      @touchcancel.prevent="syncTouches"
    >
      <canvas ref="canvas" width="400" height="400"></canvas>
      <p class="hint">在此区域触摸，上方实时显示触点信息</p>
    </div>

    <!-- 检测到 5 触点时保存并显示在下方，刷新页面清空 -->
    <div v-if="lastFivePoints && lastFivePoints.length === 5" class="last-five-info">
      <p class="last-five-title">已检测到 5 触点（标准坐标 800×600）</p>
      <p v-for="(point, idx) in lastFivePoints" :key="idx" class="point-line">
        点{{ idx + 1 }}： [{{ point[0] }}, {{ point[1] }}]
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SealTouchDetect',
  data() {
    return {
      touchPoints: [],
      /** 最近一次检测到 5 触点时的坐标，用于在触摸区域下方展示，刷新即清空 */
      lastFivePoints: null,
      canvas: null,
      ctx: null,
      standardWidth: 800,
      standardHeight: 600
    }
  },
  mounted() {
    this.canvas = this.$refs.canvas
    if (this.canvas) {
      this.ctx = this.canvas.getContext('2d')
    }
  },
  methods: {
    /**
     * 从触摸事件同步触点并重绘画布。
     * 有触点时更新坐标与红色点位；无触点时保留最后一次的坐标及画布点位，不清空。
     */
    syncTouches(e) {
      const touches = e.touches || []
      const container = this.$refs.canvasContainer
      if (!this.canvas || !this.ctx || !container) return

      if (touches.length === 0) {
        return
      }

      const containerRect = container.getBoundingClientRect()
      const canvasRect = this.canvas.getBoundingClientRect()
      const scaleToCanvasX = this.canvas.width / canvasRect.width
      const scaleToCanvasY = this.canvas.height / canvasRect.height

      this.touchPoints = []
      for (let i = 0; i < touches.length; i++) {
        const t = touches[i]
        const x = t.clientX - containerRect.left
        const y = t.clientY - containerRect.top
        const standardX = Math.round((x / containerRect.width) * this.standardWidth)
        const standardY = Math.round((y / containerRect.height) * this.standardHeight)
        this.touchPoints.push([standardX, standardY])
      }

      if (this.touchPoints.length === 5) {
        this.lastFivePoints = this.touchPoints.map(function (p) { return [p[0], p[1]] })
        console.log('[印章触点检测] 检测到5触点，标准坐标(800×600):', JSON.stringify(this.touchPoints))
      }

      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      for (let i = 0; i < touches.length; i++) {
        const t = touches[i]
        const canvasX = (t.clientX - canvasRect.left) * scaleToCanvasX
        const canvasY = (t.clientY - canvasRect.top) * scaleToCanvasY
        this.drawPoint(canvasX, canvasY, i + 1)
      }
    },
    drawPoint(x, y, index) {
      this.ctx.fillStyle = '#FF0000'
      this.ctx.beginPath()
      this.ctx.arc(x, y, 8, 0, 2 * Math.PI)
      this.ctx.fill()
      this.ctx.fillStyle = '#FFFFFF'
      this.ctx.font = 'bold 12px Arial'
      this.ctx.textAlign = 'center'
      this.ctx.textBaseline = 'middle'
      this.ctx.fillText(String(index), x, y)
    }
  }
}
</script>

<style scoped>
.seal-touch-detect-container {
  background: #f5f5f5;
  min-height: 100vh;
}

/* 固定 12 行高度，避免下方盖章区域上下移动 */
.realtime-info {
  background: #fff;
  margin: 12px;
  padding: 16px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  height: calc(12 * 1.5em);
  min-height: calc(12 * 1.5em);
  overflow-y: auto;
  box-sizing: border-box;
}

.count-line {
  margin: 0 0 12px 0;
  font-size: 16px;
}

.count-num {
  color: #1989fa;
  font-size: 20px;
}

.points-list {
  margin: 0;
}

.point-line {
  margin: 6px 0;
  font-family: monospace;
}

.coord-hint {
  color: #999;
  font-size: 12px;
}

.no-touch {
  color: #999;
  margin: 0;
}

.canvas-container {
  background: #fff;
  margin: 12px;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
}

/* 禁用浏览器默认触摸行为，减少多点被合并为 1 个点的概率 */
.touch-capture {
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

canvas {
  border: 1px solid #ddd;
  max-width: 100%;
}

.hint {
  color: #999;
  font-size: 12px;
  margin-top: 8px;
}

.last-five-info {
  background: #fff;
  margin: 12px;
  padding: 16px;
  border-radius: 8px;
  font-size: 14px;
}

.last-five-title {
  margin: 0 0 12px 0;
  font-weight: bold;
  color: #1989fa;
}

.last-five-info .point-line {
  margin: 6px 0;
  font-family: monospace;
}
</style>
